import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { apiFetch } from '../api/client';
import { dataViaApi } from '../api/mode';
import { resolveAppUser } from '../data/userRoles';
import type { AppUser } from '../data/types';
import { getActiveAccount } from './msal';
import { asSummaryDistricts, forgetCachedProfile, readCachedProfile, sameProfile, writeCachedProfile } from './profileCache';
import type { SummaryDistricts } from '../data/districtSummaries';

interface AppUserState {
  /** The EFFECTIVE user (impersonation applied). null while loading/out. */
  user: AppUser | null;
  /** The actually signed-in user, regardless of impersonation. */
  realUser: AppUser | null;
  impersonating: boolean;
  /**
   * Dev/testing aid: Admins can view the app as another user (client-side
   * only — the underlying delegated permissions are unchanged; this only
   * NARROWS what the UI shows, never widens access).
   */
  setImpersonation: (target: AppUser | null) => void;
  loading: boolean;
  /** Signed in to Entra but no active UserRoles row. */
  unauthorized: boolean;
  /** ID token hit the groups-claim overage limit; group-based roles couldn't be evaluated. */
  groupsOverage: boolean;
  /**
   * Districts the server keeps monthly summaries for that this account sees
   * in full (/api/me; '*' = all). null = unknown (direct mode, older server)
   * → pages read audits (data/districtSummaries.ts summariesCover).
   */
  summaryDistricts: SummaryDistricts;
  error: string | null;
}

const AppUserContext = createContext<AppUserState>({
  user: null,
  realUser: null,
  impersonating: false,
  setImpersonation: () => undefined,
  loading: false,
  unauthorized: false,
  groupsOverage: false,
  summaryDistricts: null,
  error: null,
});

/** Not audit data — just a dev-mode UI preference; sessionStorage is fine. */
const IMPERSONATION_KEY = 'sspaudit.impersonation';

function loadStoredImpersonation(): AppUser | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as AppUser) : null;
  } catch {
    return null;
  }
}

interface GroupClaims {
  groups?: unknown;
  _claim_names?: { groups?: unknown };
}

/**
 * Entra group object IDs from the ID token. Requires the groups claim to be
 * configured on the app registration (docs/entra-setup.md §6); returns []
 * when absent. `_claim_names.groups` signals overage (user in >200 groups):
 * the claim is omitted and group-based roles can't be evaluated this way.
 */
function extractGroupIds(claims: unknown): {
  groupIds: string[];
  overage: boolean;
} {
  const c = (claims ?? {}) as GroupClaims;
  const overage = c._claim_names?.groups !== undefined;
  const groupIds = Array.isArray(c.groups)
    ? c.groups.filter((g): g is string => typeof g === 'string')
    : [];
  return { groupIds, overage };
}

interface ResolutionState {
  realUser: AppUser | null;
  loading: boolean;
  unauthorized: boolean;
  groupsOverage: boolean;
  summaryDistricts: SummaryDistricts;
  error: string | null;
}

export function AppUserProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  // The account whose token every /api call carries (msal.ts acquireToken),
  // so the profile — and the sync engine's owner — always match it.
  const account = accounts.length > 0 ? (getActiveAccount() ?? accounts[0]) : undefined;
  // The last profile this device saw for the account opens the app at once
  // (profileCache.ts); /api/me re-checks it right after.
  const [state, setState] = useState<ResolutionState>(() => {
    const cached = isAuthenticated && account ? readCachedProfile(account.username) : null;
    return {
      realUser: cached?.user ?? null,
      loading: cached === null,
      unauthorized: false,
      groupsOverage: cached?.groupsOverage ?? false,
      summaryDistricts: cached?.summaryDistricts ?? null,
      error: null,
    };
  });
  const [impersonated, setImpersonated] = useState<AppUser | null>(
    loadStoredImpersonation,
  );

  useEffect(() => {
    if (!isAuthenticated || !account) {
      setState({
        realUser: null,
        loading: false,
        unauthorized: false,
        groupsOverage: false,
        summaryDistricts: null,
        error: null,
      });
      return;
    }
    let cancelled = false;
    const username = account.username;
    const cached = readCachedProfile(username);
    // Already showing this very profile (the first render read it) → keep
    // the same state object, so pages that load on `user` load once.
    const cachedDistricts = cached?.summaryDistricts ?? null;
    setState((s) =>
      cached
        ? showing(s, cached.user, cached.groupsOverage, cachedDistricts)
          ? s
          : { realUser: cached.user, loading: false, unauthorized: false, groupsOverage: cached.groupsOverage, summaryDistricts: cachedDistricts, error: null }
        : { ...s, realUser: null, loading: true, summaryDistricts: null, error: null }, // never another account's profile meanwhile
    );
    const started = performance.now();
    const { groupIds, overage } = extractGroupIds(account.idTokenClaims);
    // Server mode: the SERVER resolves the role from the access token (its
    // own groups claim), so the app shows exactly what the server enforces.
    const resolution: Promise<{ user: AppUser | null; groupsOverage: boolean; summaryDistricts?: unknown }> =
      dataViaApi()
        ? apiFetch('/me')
        : resolveAppUser(
            account.username,
            account.name ?? account.username,
            groupIds,
          ).then((user) => ({ user, groupsOverage: overage }));
    resolution
      .then(({ user: realUser, groupsOverage, summaryDistricts: rawDistricts }) => {
        // Direct mode has no server summaries: never usable there.
        const districts = (dataViaApi() ? asSummaryDistricts(rawDistricts) : undefined) ?? null;
        if (realUser) writeCachedProfile(username, { user: realUser, groupsOverage, ...(districts ? { summaryDistricts: districts } : {}) });
        else forgetCachedProfile(username);
        if (cancelled) return;
        logProfileTiming(started, cached !== null);
        // Unchanged since the saved copy → keep the same object, so pages
        // that load on `user` don't all load a second time.
        setState((s) =>
          realUser && showing(s, realUser, groupsOverage, districts)
            ? s
            : { realUser, loading: false, unauthorized: realUser === null, groupsOverage, summaryDistricts: districts, error: null },
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (cached) {
          // Keep working from this device's copy (e.g. offline at a store);
          // every data request is still checked by the server.
          console.warn('Could not refresh your profile; using the copy saved on this device.', err);
          return;
        }
        setState({
          realUser: null,
          loading: false,
          unauthorized: false,
          groupsOverage: overage,
          summaryDistricts: null,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account?.username]);

  const isAdmin = state.realUser?.role === 'Admin';

  const setImpersonation = (target: AppUser | null) => {
    if (target !== null && !isAdmin) return; // Admins only
    setImpersonated(target);
    try {
      if (target) {
        sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(target));
      } else {
        sessionStorage.removeItem(IMPERSONATION_KEY);
      }
    } catch {
      // Storage unavailable — impersonation just won't survive reloads.
    }
  };

  // A stored impersonation only applies while the real user is an Admin.
  const effectiveImpersonation = isAdmin ? impersonated : null;

  const value: AppUserState = {
    user: effectiveImpersonation ?? state.realUser,
    realUser: state.realUser,
    impersonating: effectiveImpersonation !== null,
    setImpersonation,
    loading: state.loading,
    unauthorized: state.unauthorized,
    groupsOverage: state.groupsOverage,
    summaryDistricts: state.summaryDistricts,
    error: state.error,
  };

  return (
    <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>
  );
}

const sameDistricts = (a: SummaryDistricts, b: SummaryDistricts) =>
  a === b || (Array.isArray(a) && Array.isArray(b) && a.join('\u0000') === b.join('\u0000'));

/** The state already shows exactly this profile, settled. */
function showing(s: ResolutionState, user: AppUser, groupsOverage: boolean, districts: SummaryDistricts): boolean {
  return (
    s.realUser !== null &&
    sameProfile(s.realUser, user) &&
    s.groupsOverage === groupsOverage &&
    sameDistricts(s.summaryDistricts, districts) &&
    !s.loading &&
    !s.unauthorized &&
    s.error === null
  );
}

/**
 * Where the wait before the app opens goes (owner report 2026-10-19:
 * "Loading your profile" ~10 s). One line in the browser console.
 */
function logProfileTiming(started: number, fromCache: boolean): void {
  const signInReady = performance.getEntriesByName('auditplus:sign-in-ready')[0]?.startTime;
  const parts = [
    signInReady !== undefined ? `page → sign-in ready ${Math.round(signInReady)} ms` : null,
    `profile from the server ${Math.round(performance.now() - started)} ms`,
    fromCache ? 'app opened from the copy on this device meanwhile' : null,
  ].filter(Boolean);
  console.info(`[AuditPlus] ${parts.join(' · ')}`);
}

export function useAppUser(): AppUserState {
  return useContext(AppUserContext);
}
